import React, { useCallback, useEffect, useState } from 'react';
import api from './api';
import {
  formatDate,
  isoToDisplayDate,
  displayDateToIso,
  userDisplayName,
  CAN_MANAGE_ABSENCES_ROLES,
} from './helpers';

const LIGNE_VIDE = { date_debut: '', date_fin: '', interimaire_id: '', motif: 'Rotation' };

/**
 * Rotations et intérim.
 *
 * Sur le site, chacun alterne 20 jours de travail et 10 jours de repos. Sans
 * intérimaire désigné, les tâches — et surtout les validations en attente —
 * restent sans titulaire pendant dix jours. Déclarer sa rotation à l'avance
 * suffit à ce que tout bascule automatiquement.
 *
 * `pourUtilisateur` permet à la direction et aux chefs de projet de gérer
 * l'absence de quelqu'un d'autre ; sans lui, chacun gère la sienne.
 */
function AbsencesPanel({ currentUser, users, pourUtilisateur, compact = false }) {
  const cible = pourUtilisateur || currentUser;
  const [absences, setAbsences] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [formulaire, setFormulaire] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(() => {
    setChargement(true);
    api
      .get('/absences', { params: { userId: cible.id, avenir: '1' } })
      .then((res) => setAbsences(res.data))
      .catch(() => setErreur('Impossible de charger les absences.'))
      .finally(() => setChargement(false));
  }, [cible.id]);

  useEffect(() => { charger(); }, [charger]);

  const peutGerer =
    cible.id === currentUser?.id || CAN_MANAGE_ABSENCES_ROLES.includes(currentUser?.role);

  const champ = (nom) => (e) => setFormulaire({ ...formulaire, [nom]: e.target.value });

  const enregistrer = (e) => {
    e.preventDefault();
    setErreur('');

    const debut = displayDateToIso(formulaire.date_debut);
    const fin = displayDateToIso(formulaire.date_fin);
    if (!debut) return setErreur('La date de début doit être au format jj/mm/aaaa.');
    if (!fin) return setErreur('La date de fin doit être au format jj/mm/aaaa.');
    if (debut > fin) return setErreur('La date de début est postérieure à la date de fin.');
    if (!formulaire.interimaire_id) {
      return setErreur('Désignez un intérimaire : c’est tout l’intérêt de déclarer l’absence.');
    }

    const corps = {
      user_id: cible.id,
      date_debut: formulaire.date_debut,
      date_fin: formulaire.date_fin,
      interimaire_id: formulaire.interimaire_id,
      motif: formulaire.motif,
    };

    setEnvoi(true);
    const requete = formulaire.id
      ? api.put(`/absences/${formulaire.id}`, corps)
      : api.post('/absences', corps);

    requete
      .then(() => { setFormulaire(null); charger(); })
      .catch((err) => setErreur(err.response?.data?.message || "L'enregistrement a échoué."))
      .finally(() => setEnvoi(false));
  };

  const supprimer = (a) => {
    setErreur('');
    api
      .delete(`/absences/${a.id}`)
      .then(charger)
      .catch((err) => setErreur(err.response?.data?.message || 'La suppression a échoué.'));
  };

  const ouvrir = (a) => {
    setErreur('');
    setFormulaire(
      a
        ? {
            ...a,
            date_debut: isoToDisplayDate(a.date_debut),
            date_fin: isoToDisplayDate(a.date_fin),
            interimaire_id: a.interimaire_id || '',
          }
        : { ...LIGNE_VIDE }
    );
  };

  if (chargement) return <p>Chargement des rotations...</p>;

  return (
    <div>
      {!compact && (
        <div className="page-header-row">
          <h3 style={{ fontSize: 14, margin: 0 }}>
            Rotations et intérim {pourUtilisateur && `— ${userDisplayName(cible)}`}
          </h3>
          {peutGerer && !formulaire && (
            <button className="btn btn-primary btn-sm" onClick={() => ouvrir(null)}>
              + Déclarer une absence
            </button>
          )}
        </div>
      )}

      {erreur && <div className="error-message" style={{ marginBottom: 12 }}>{erreur}</div>}

      {formulaire && (
        <form className="card" style={{ marginBottom: 12 }} onSubmit={enregistrer}>
          <div className="form-row">
            <div className="form-group">
              <label>Du</label>
              <input placeholder="jj/mm/aaaa" value={formulaire.date_debut} onChange={champ('date_debut')} />
            </div>
            <div className="form-group">
              <label>Au</label>
              <input placeholder="jj/mm/aaaa" value={formulaire.date_fin} onChange={champ('date_fin')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Intérimaire</label>
              <select value={formulaire.interimaire_id} onChange={champ('interimaire_id')}>
                <option value="">— Choisir —</option>
                {users
                  .filter((u) => u.id !== cible.id && u.active !== false)
                  .map((u) => (
                    <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label>Motif</label>
              <input value={formulaire.motif} onChange={champ('motif')} placeholder="Rotation, congé, mission..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={() => setFormulaire(null)}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={envoi}>
              {envoi ? 'Enregistrement...' : formulaire.id ? 'Mettre à jour' : 'Enregistrer'}
            </button>
          </div>
        </form>
      )}

      {absences.length === 0 && !formulaire && (
        <div className="empty-state" style={{ textAlign: 'left' }}>
          Aucune absence déclarée. Déclarez vos rotations à l’avance : vos tâches et
          vos validations basculeront automatiquement vers votre intérimaire.
          {compact && peutGerer && (
            <div style={{ marginTop: 10 }}>
              <button className="btn btn-sm" onClick={() => ouvrir(null)}>Déclarer une absence</button>
            </div>
          )}
        </div>
      )}

      {absences.length > 0 && (
        <div className="table-container">
          <table className="rapport-tableau">
            <thead>
              <tr>
                <th>Période</th>
                <th>Intérimaire</th>
                <th>Motif</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {absences.map((a) => (
                <tr key={a.id}>
                  <td>
                    {formatDate(a.date_debut)} → {formatDate(a.date_fin)}
                    {a.en_cours && <span className="badge badge-active" style={{ marginLeft: 8 }}>en cours</span>}
                  </td>
                  <td>{a.interimaire_name || <span className="rapport-retard">non désigné</span>}</td>
                  <td>{a.motif || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {peutGerer && (
                      <>
                        <button className="btn btn-sm" onClick={() => ouvrir(a)}>Modifier</button>
                        <button className="btn btn-sm btn-danger" style={{ marginLeft: 6 }} onClick={() => supprimer(a)}>
                          Supprimer
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {compact && peutGerer && absences.length > 0 && !formulaire && (
        <button className="btn btn-sm" style={{ marginTop: 10 }} onClick={() => ouvrir(null)}>
          + Déclarer une absence
        </button>
      )}
    </div>
  );
}

export default AbsencesPanel;
